import {
	Badge,
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui';
import { PromotionStatusEnum } from '@/enums';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MoreHorizontal, Trash2, Clock, Play, CheckCircle, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IPromotion } from '@/interfaces';
import { PROMOTION_PRODUCT_LABELS, PROMOTION_STATUS_CONFIG } from '@/constants';

const STATUS_ACTIONS: { status: PromotionStatusEnum; label: string; icon: React.ReactNode }[] = [
	{ status: PromotionStatusEnum.PENDING, label: 'En attente', icon: <Clock className="mr-2 h-4 w-4" /> },
	{ status: PromotionStatusEnum.ACTIVE, label: 'En cours', icon: <Play className="mr-2 h-4 w-4" /> },
	{ status: PromotionStatusEnum.FINISHED, label: 'Terminer', icon: <CheckCircle className="mr-2 h-4 w-4" /> }
];

type TProps = {
	promotion: IPromotion;
	onSetStatus: (promotion: IPromotion, status: PromotionStatusEnum) => void;
	onDelete: (promotion: IPromotion) => void;
	onViewDetails: (promotion: IPromotion) => void;
	canDelete: boolean;
};

const formatDate = (d: string) => {
	try {
		return format(new Date(d), 'dd MMM yyyy', { locale: fr });
	} catch {
		return d;
	}
};

const DataTableMobileCard = ({ promotion, onSetStatus, onDelete, onViewDetails, canDelete }: TProps) => {
	const statusCfg = PROMOTION_STATUS_CONFIG[promotion.status] ?? PROMOTION_STATUS_CONFIG[PromotionStatusEnum.PENDING];

	return (
		<div
			className="px-4 py-4 border rounded-lg bg-card flex items-center gap-4 cursor-pointer"
			onClick={() => onViewDetails(promotion)}
		>
			<div className="flex-1 min-w-0 space-y-2">
				<div className="flex items-center gap-1.5 flex-wrap">
					<span className="text-sm font-semibold">{promotion.percentage}%</span>
					{promotion.products.map(p => (
						<Badge key={p} variant="secondary" className="text-xs">
							{PROMOTION_PRODUCT_LABELS[p] ?? p}
						</Badge>
					))}
				</div>

				<p className="text-xs text-muted-foreground">
					{formatDate(promotion.from)} → {formatDate(promotion.to)}
				</p>
				<div className="flex justify-end">
					<Badge className={cn('text-xs', statusCfg.className)}>{statusCfg.label}</Badge>
				</div>
			</div>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0">
						<span className="sr-only">Actions</span>
						<MoreHorizontal size={14} />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					<DropdownMenuItem
						className="cursor-pointer"
						onClick={e => {
							e.stopPropagation();
							onViewDetails(promotion);
						}}
					>
						<Eye className="mr-2 h-4 w-4" />
						Voir
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					{promotion.status !== PromotionStatusEnum.FINISHED && STATUS_ACTIONS.filter(a => a.status !== promotion.status).map(a => (
						<DropdownMenuItem
							key={a.status}
							className="cursor-pointer"
							onClick={e => {
								e.stopPropagation();
								onSetStatus(promotion, a.status);
							}}
						>
							{a.icon}
							{a.label}
						</DropdownMenuItem>
					))}
					{canDelete && (
						<>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								className="text-destructive cursor-pointer"
								onClick={e => {
									e.stopPropagation();
									onDelete(promotion);
								}}
							>
								<Trash2 className="mr-2 h-4 w-4" />
								Supprimer
							</DropdownMenuItem>
						</>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
};

export default DataTableMobileCard;
